import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { getNotifications } from '../firebase';
import { Notification } from '../types';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useAuth } from './AuthContext';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  hasMore: boolean;
  error: Error | null;
  loadMore: () => Promise<void>;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  isRead: (notificationId: string) => boolean;
  refresh: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  loading: false,
  hasMore: false,
  error: null,
  loadMore: async () => {},
  markAsRead: () => {},
  markAllAsRead: () => {},
  isRead: () => false,
  refresh: async () => {},
});

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [readNotifications, setReadNotifications] = useState<Set<string>>(() => {
    // Load read notifications from localStorage
    const stored = localStorage.getItem('readNotifications');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  // Calculate unread count
  const unreadCount = notifications.filter(notification => !readNotifications.has(notification.id)).length;

  // Load initial notifications only when the user is logged in
  useEffect(() => {
    const fetchInitialNotifications = async () => {
      // Only fetch notifications if the user is logged in
      if (!currentUser) {
        return;
      }
      
      try {
        setLoading(true);
        const result = await getNotifications();
        setNotifications(result.notifications);
        setLastVisible(result.lastVisible);
        setHasMore(!!result.lastVisible);
      } catch (err: any) {
        console.error('Error loading notifications:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialNotifications();
  }, [currentUser]);

  // Set up periodic refresh every minute, but only if user is logged in
  useEffect(() => {
    // Don't refresh if user is not logged in
    if (!currentUser) {
      return;
    }
    
    const intervalId = setInterval(() => {
      // Only refresh if not already loading
      if (!loading) {
        refresh();
      }
    }, 60000); // Refresh every 60 seconds

    // Clear interval on unmount
    return () => clearInterval(intervalId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, currentUser]);

  // Save read notifications to localStorage when updated
  useEffect(() => {
    localStorage.setItem('readNotifications', JSON.stringify(Array.from(readNotifications)));
  }, [readNotifications]);

  // Load more notifications
  const loadMore = async () => {
    if (!hasMore || loading || !lastVisible || !currentUser) return;

    try {
      setLoading(true);
      const result = await getNotifications(lastVisible);
      setNotifications(prev => [...prev, ...result.notifications]);
      setLastVisible(result.lastVisible);
      setHasMore(!!result.lastVisible);
    } catch (err: any) {
      console.error('Error loading more notifications:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // Mark a notification as read
  const markAsRead = (notificationId: string) => {
    setReadNotifications(prev => {
      const newSet = new Set(prev);
      newSet.add(notificationId);
      return newSet;
    });
  };

  // Mark all notifications as read
  const markAllAsRead = () => {
    const allIds = notifications.map(notification => notification.id);
    setReadNotifications(prev => {
      const newSet = new Set(prev);
      allIds.forEach(id => newSet.add(id));
      return newSet;
    });
  };

  // Check if a notification is read
  const isRead = (notificationId: string) => {
    return readNotifications.has(notificationId);
  };

  // Refresh notifications
  const refresh = async () => {
    // Don't refresh if user is not logged in
    if (!currentUser) {
      return;
    }
    
    try {
      setLoading(true);
      setNotifications([]);
      setLastVisible(null);
      const result = await getNotifications();
      setNotifications(result.notifications);
      setLastVisible(result.lastVisible);
      setHasMore(!!result.lastVisible);
    } catch (err: any) {
      console.error('Error refreshing notifications:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    hasMore,
    error,
    loadMore,
    markAsRead,
    markAllAsRead,
    isRead,
    refresh,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}; 